import { useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { ChevronDown, ChevronUp, Plus, Trash2 } from 'lucide-react';
import { useAuthedApi } from '../../lib/useAuthedApi';
import Toggle from '../../components/Toggle';
import {
  type PreviewMeetingType,
  PlaceholderButtons,
  MeetingTypeSeg,
  EmailMessagePreview,
  SmsMessagePreview,
  insertTokenAt,
  defaultReminderEmailBody,
  defaultReminderSmsText,
} from '../../lib/messagePreview';

/**
 * Org Settings → Reminders. Lets an admin configure when reminders go out
 * before a booking (offset + channel) and customize the email/SMS copy per
 * reminder. Backed by /organizations/me/reminder-rules. An empty list means
 * no reminders are sent.
 */

type Channel = 'email' | 'sms' | 'both';

type ReminderRule = {
  id: string;
  offset_minutes: number;
  channel: Channel;
  email_subject: string | null;
  email_body: string | null;
  sms_body: string | null;
  enabled: number;
};

type Unit = 'minutes' | 'hours' | 'days';

const MAX_RULES = 8;

const CHANNEL_LABEL: Record<Channel, string> = {
  both: 'Email + SMS',
  email: 'Email only',
  sms: 'SMS only',
};

function splitOffset(min: number): { value: number; unit: Unit } {
  if (min % 1440 === 0) return { value: min / 1440, unit: 'days' };
  if (min % 60 === 0) return { value: min / 60, unit: 'hours' };
  return { value: min, unit: 'minutes' };
}

function toMinutes(value: number, unit: Unit): number {
  if (unit === 'days') return value * 1440;
  if (unit === 'hours') return value * 60;
  return value;
}

function humanizeOffset(min: number): string {
  const { value, unit } = splitOffset(min);
  const u = value === 1 ? unit.slice(0, -1) : unit;
  return `${value} ${u} before`;
}

function errMsg(e: unknown): string {
  const body = (e as { body?: { error?: string; token?: string; max?: number } }).body;
  if (body?.error === 'unknown_token' && body.token) {
    return `Unknown placeholder: {{${body.token}}}. Check the supported placeholders below.`;
  }
  if (body?.error === 'too_many_rules') {
    return `You can have at most ${body.max ?? MAX_RULES} reminders.`;
  }
  if (body?.error === 'rule_exists') {
    return 'A reminder with that timing and channel already exists.';
  }
  return (e as Error).message;
}

export default function RemindersCard({
  brandName,
  brandLogoUrl,
}: {
  brandName: string;
  brandLogoUrl: string | null;
}) {
  const apiFn = useAuthedApi();
  const [rules, setRules] = useState<ReminderRule[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // Add-form state
  const [adding, setAdding] = useState(false);
  const [newValue, setNewValue] = useState(24);
  const [newUnit, setNewUnit] = useState<Unit>('hours');
  const [newChannel, setNewChannel] = useState<Channel>('both');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiFn<ReminderRule[]>('/organizations/me/reminder-rules')
      .then(setRules)
      .catch((e) => setErr(errMsg(e)));
  }, [apiFn]);

  async function addRule() {
    setSaving(true);
    setErr(null);
    try {
      const created = await apiFn<ReminderRule>('/organizations/me/reminder-rules', {
        method: 'POST',
        body: {
          offset_minutes: toMinutes(newValue, newUnit),
          channel: newChannel,
        },
      });
      setRules((rs) =>
        [...(rs ?? []), created].sort((a, b) => b.offset_minutes - a.offset_minutes),
      );
      setAdding(false);
      setNewValue(24);
      setNewUnit('hours');
      setNewChannel('both');
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setSaving(false);
    }
  }

  function onChanged(updated: ReminderRule) {
    setRules((rs) =>
      (rs ?? [])
        .map((r) => (r.id === updated.id ? updated : r))
        .sort((a, b) => b.offset_minutes - a.offset_minutes),
    );
  }

  function onDeleted(id: string) {
    setRules((rs) => (rs ?? []).filter((r) => r.id !== id));
  }

  return (
    <div className="card">
      <h2 style={{ marginTop: 0 }}>Reminders</h2>
      <p className="muted" style={{ marginTop: 0 }}>
        Set when to remind customers before their appointment, and what each
        one says. None listed means none sent.
      </p>

      {err && <p className="err">{err}</p>}

      {rules === null ? (
        <p className="muted">Loading…</p>
      ) : rules.length === 0 ? (
        <p className="muted">No reminders yet — add one below.</p>
      ) : (
        <div>
          {rules.map((rule) => (
            <RuleRow
              key={rule.id}
              rule={rule}
              brandName={brandName}
              brandLogoUrl={brandLogoUrl}
              onChanged={onChanged}
              onDeleted={onDeleted}
            />
          ))}
        </div>
      )}

      {adding ? (
        <div className="field-section" style={{ marginTop: '1rem' }}>
          <h3>New reminder</h3>
          <div className="row" style={{ gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <span>Send</span>
            <input
              type="number"
              min={1}
              value={newValue}
              onChange={(e) => setNewValue(Math.max(1, Number(e.target.value) || 1))}
              style={{ width: 80 }}
            />
            <select value={newUnit} onChange={(e) => setNewUnit(e.target.value as Unit)}>
              <option value="minutes">minutes</option>
              <option value="hours">hours</option>
              <option value="days">days</option>
            </select>
            <span>before, via</span>
            <select
              value={newChannel}
              onChange={(e) => setNewChannel(e.target.value as Channel)}
            >
              <option value="both">Email + SMS</option>
              <option value="email">Email only</option>
              <option value="sms">SMS only</option>
            </select>
          </div>
          <div className="row" style={{ gap: '0.5rem', marginTop: '0.75rem' }}>
            <button className="primary" onClick={addRule} disabled={saving}>
              {saving ? 'Adding…' : 'Add reminder'}
            </button>
            <button className="ghost" onClick={() => setAdding(false)} disabled={saving}>
              Cancel
            </button>
          </div>
          <p className="field-help">
            You can customize this reminder&rsquo;s wording after adding it.
          </p>
        </div>
      ) : (
        <button
          className="ghost"
          onClick={() => setAdding(true)}
          disabled={(rules?.length ?? 0) >= MAX_RULES}
          style={{ marginTop: '1rem' }}
        >
          <Plus size={14} /> Add reminder
        </button>
      )}
      {(rules?.length ?? 0) >= MAX_RULES && (
        <p className="field-help">You&rsquo;ve reached the maximum of {MAX_RULES} reminders.</p>
      )}
    </div>
  );
}

function RuleRow({
  rule,
  brandName,
  brandLogoUrl,
  onChanged,
  onDeleted,
}: {
  rule: ReminderRule;
  brandName: string;
  brandLogoUrl: string | null;
  onChanged: (r: ReminderRule) => void;
  onDeleted: (id: string) => void;
}) {
  const apiFn = useAuthedApi();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Draft copy state, hydrated from the rule.
  const [subject, setSubject] = useState(rule.email_subject ?? '');
  const [body, setBody] = useState(rule.email_body ?? '');
  const [sms, setSms] = useState(rule.sms_body ?? '');
  const [previewType, setPreviewType] = useState<PreviewMeetingType>('in_person');
  const subjectRef = useRef<HTMLInputElement>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const smsRef = useRef<HTMLTextAreaElement>(null);

  function insert(
    ref: RefObject<HTMLTextAreaElement | HTMLInputElement>,
    current: string,
    setter: (v: string) => void,
    token: string,
  ) {
    const { next, caret } = insertTokenAt(ref.current, current, token);
    setter(next);
    requestAnimationFrame(() => {
      const el = ref.current;
      if (el) {
        el.focus();
        el.setSelectionRange(caret, caret);
      }
    });
  }

  const showEmail = rule.channel === 'email' || rule.channel === 'both';
  const showSms = rule.channel === 'sms' || rule.channel === 'both';

  async function patch(fields: Record<string, unknown>) {
    setBusy(true);
    setErr(null);
    try {
      const updated = await apiFn<ReminderRule>(`/organizations/me/reminder-rules/${rule.id}`, {
        method: 'PATCH',
        body: fields,
      });
      onChanged(updated);
      return true;
    } catch (e) {
      setErr(errMsg(e));
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function toggleEnabled() {
    await patch({ enabled: !rule.enabled });
  }

  async function saveCopy() {
    await patch({
      email_subject: subject.trim() || null,
      email_body: body.trim() || null,
      sms_body: sms.trim() || null,
    });
  }

  async function remove() {
    if (!confirm(`Remove the reminder ${humanizeOffset(rule.offset_minutes)}?`)) return;
    setBusy(true);
    setErr(null);
    try {
      await apiFn(`/organizations/me/reminder-rules/${rule.id}`, { method: 'DELETE' });
      onDeleted(rule.id);
    } catch (e) {
      setErr(errMsg(e));
      setBusy(false);
    }
  }

  return (
    <div
      style={{
        border: '1px solid #eaeaea',
        borderRadius: 6,
        padding: '0.75rem 0.9rem',
        marginBottom: '0.6rem',
        opacity: rule.enabled ? 1 : 0.6,
      }}
    >
      <div className="row" style={{ alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <strong>{humanizeOffset(rule.offset_minutes)}</strong>{' '}
          <span className="muted">· {CHANNEL_LABEL[rule.channel]}</span>
        </div>
        <div className="row" style={{ gap: '0.5rem', alignItems: 'center' }}>
          <Toggle
            checked={!!rule.enabled}
            onChange={toggleEnabled}
            disabled={busy}
            label={<span className="muted">{rule.enabled ? 'On' : 'Off'}</span>}
          />
          <button className="ghost" onClick={() => setOpen((o) => !o)} disabled={busy}>
            {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />} Messages
          </button>
          <button className="danger" onClick={remove} disabled={busy} aria-label="Remove reminder">
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      {err && <p className="err" style={{ marginBottom: 0 }}>{err}</p>}

      {open && (
        <div style={{ marginTop: '0.75rem' }}>
          <MeetingTypeSeg value={previewType} onChange={setPreviewType} />

          {showEmail && (
            <>
              <div className="field-section">
                <h3>Email subject</h3>
                <PlaceholderButtons onInsert={(t) => insert(subjectRef, subject, setSubject, t)} />
                <input
                  ref={subjectRef}
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Reminder: your appointment is coming up"
                  maxLength={200}
                />
              </div>
              <div className="field-section">
                <h3>Email message</h3>
                <PlaceholderButtons onInsert={(t) => insert(bodyRef, body, setBody, t)} />
                <textarea
                  ref={bodyRef}
                  rows={4}
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder="Hi {{bookee_name}}, this is a reminder of your {{service_name}} on {{start_time}}."
                  maxLength={2000}
                  style={{ fontFamily: 'inherit' }}
                />
                <p className="field-help">
                  This becomes the reminder email&rsquo;s message; the greeting,
                  signature, and footer are added automatically. Leave blank to use
                  the default shown below.
                </p>
                <EmailMessagePreview
                  brandName={brandName}
                  brandLogoUrl={brandLogoUrl}
                  body={body}
                  previewType={previewType}
                  fallbackBody={defaultReminderEmailBody(rule.offset_minutes, previewType)}
                />
              </div>
            </>
          )}
          {showSms && (
            <div className="field-section">
              <h3>Text (SMS) message</h3>
              <PlaceholderButtons onInsert={(t) => insert(smsRef, sms, setSms, t)} />
              <textarea
                ref={smsRef}
                rows={2}
                value={sms}
                onChange={(e) => setSms(e.target.value)}
                placeholder="Reminder: {{service_name}} with {{staff_name}} on {{start_time}}."
                maxLength={320}
                style={{ fontFamily: 'inherit' }}
              />
              <p className="field-help">
                Keep it short — long texts send as multiple segments. Leave blank
                to use the default shown below.
              </p>
              <SmsMessagePreview
                text={sms}
                previewType={previewType}
                fallbackText={defaultReminderSmsText(rule.offset_minutes, brandName)}
              />
            </div>
          )}
          <button className="primary" onClick={saveCopy} disabled={busy} style={{ marginTop: '0.5rem' }}>
            {busy ? 'Saving…' : 'Save messages'}
          </button>
        </div>
      )}
    </div>
  );
}
